import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OrgStatus } from '@tips-sso/shared';
import { PageHeader } from '../components/PageHeader.js';
import { StatusBadge, Field } from '../components/Field.js';
import { Loading, ErrorState } from '../components/States.js';
import { errorMessage } from '../api/client.js';
import { fetchOrg, updateOrg, type UpdateOrgInput } from '../api/endpoints.js';

const ORG_STATUSES: OrgStatus[] = ['active', 'trial', 'suspended', 'terminated'];

export function OrgDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const org = useQuery({ queryKey: ['org', id], queryFn: () => fetchOrg(id) });

  const [form, setForm] = useState<UpdateOrgInput>({});
  useEffect(() => {
    if (org.data?.org) {
      setForm({
        displayName: org.data.org.displayName,
        primaryDomain: org.data.org.primaryDomain,
        status: org.data.org.status,
      });
    }
  }, [org.data]);

  const mutation = useMutation({
    mutationFn: () => updateOrg(id, form),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org', id] });
      void qc.invalidateQueries({ queryKey: ['orgs'] });
    },
  });

  if (org.isLoading) return <Loading />;
  if (org.isError || !org.data?.org) {
    return <ErrorState error={org.error} onRetry={() => void org.refetch()} />;
  }

  const o = org.data.org;
  const isCustomer = o.orgType === 'direct_customer' || o.orgType === 'reseller_customer';
  const isReseller = o.orgType === 'reseller';

  return (
    <div className="page">
      <PageHeader
        title={o.displayName}
        subtitle={o.legalName}
        leading={
          <Link className="back-arrow" to="/tipsconnect/admin/orgs">
            ‹ Organizations
          </Link>
        }
        actions={<StatusBadge status={o.status} />}
      />

      <div className="panel-grid">
        <section className="card">
          <h3 className="card__title">Details</h3>
          <dl className="kv">
            <dt>Org ID</dt>
            <dd>
              <code>{o.id}</code>
            </dd>
            <dt>ZITADEL org</dt>
            <dd>
              <code>{o.zitadelOrgId}</code>
            </dd>
            <dt>Type</dt>
            <dd>{o.orgType}</dd>
            <dt>Created</dt>
            <dd>{new Date(o.createdAt).toLocaleString()}</dd>
          </dl>
        </section>

        <section className="card">
          <h3 className="card__title">Edit</h3>
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              mutation.mutate();
            }}
          >
            {mutation.isError && <div className="form-error">{errorMessage(mutation.error)}</div>}
            {mutation.isSuccess && <div className="notice notice--info">Saved.</div>}
            <Field label="Display name">
              <input
                className="field__input"
                value={form.displayName ?? ''}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              />
            </Field>
            <Field label="Primary domain">
              <input
                className="field__input"
                value={form.primaryDomain ?? ''}
                onChange={(e) =>
                  setForm({ ...form, primaryDomain: e.target.value || null })
                }
              />
            </Field>
            <Field label="Status">
              <select
                className="field__select"
                value={form.status ?? o.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as OrgStatus })}
              >
                {ORG_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <div className="form__actions">
              <button type="submit" className="btn btn--primary" disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </section>
      </div>

      {(isCustomer || isReseller) && (
        <section className="card">
          <h3 className="card__title">Manage</h3>
          <div className="toolbar">
            {isReseller && (
              <Link
                className="btn btn--secondary btn--sm"
                to={`/tipsconnect/admin/resellers/${o.id}/customers`}
              >
                Reseller Customers
              </Link>
            )}
            {isCustomer && (
              <>
                <Link
                  className="btn btn--secondary btn--sm"
                  to={`/tipsconnect/admin/customers/${o.id}/users`}
                >
                  Users
                </Link>
                <Link
                  className="btn btn--secondary btn--sm"
                  to={`/tipsconnect/admin/customers/${o.id}/entitlements`}
                >
                  Entitlements
                </Link>
                <Link
                  className="btn btn--secondary btn--sm"
                  to={`/tipsconnect/admin/customers/${o.id}/identity`}
                >
                  Identity Maturity
                </Link>
                <Link
                  className="btn btn--secondary btn--sm"
                  to={`/tipsconnect/admin/customers/${o.id}/microsoft`}
                >
                  Microsoft
                </Link>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
